'use client';

/**
 * WorkItemAgentBadge — surfaces custom_fields.agent (GavShell issue-work
 * state) on a work item: status chip, PR link, needs-you callout, and a
 * deep link into the agent's issues view (?view=issues&item=<num>).
 * Renders nothing on items the agent has never touched.
 */

import React from 'react';
import { Bot, ExternalLink, GitPullRequest, Hand } from 'lucide-react';
import type { WorkItem } from './UniversalWorkItems';
import { gavShellBase } from '@/lib/shellBase';

// gavShellBase resolves the agent plane from the current host — shared with
// MyShell so every embed routes identically (see src/lib/shellBase.ts).

interface AgentState {
  status?: string;
  branch?: string;
  pr_url?: string;
  summary?: string;
  conversation?: string;
  action_required?: { kind?: string; detail?: string } | null;
}

function agentOf(item: WorkItem): AgentState | null {
  const cf = item.custom_fields;
  const agent = (cf as Record<string, unknown> | undefined)?.agent;
  return agent && typeof agent === 'object' ? agent as AgentState : null;
}

/** Compact chip variant for dense surfaces (kanban cards, list rows). */
export function WorkItemAgentChip({ workItem }: { workItem: WorkItem }) {
  const agent = agentOf(workItem);
  if (!agent?.status) return null;
  const st = agent.status;
  const label =
    st === 'working' || st === 'queued' ? 'agent…' :
    st === 'awaiting_human' ? 'needs you' :
    st === 'pr_opened' ? 'PR ready' :
    st === 'done' || st === 'fixed' ? 'agent done' :
    st === 'failed' || st === 'blocked' ? 'agent failed' : 'agent';
  const cls =
    st === 'working' || st === 'queued' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' :
    st === 'awaiting_human' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' :
    st === 'pr_opened' || st === 'done' || st === 'fixed' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' :
    st === 'failed' || st === 'blocked' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300' :
    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${cls} inline-flex items-center gap-0.5`}
      title={agent.action_required?.detail || agent.summary || `agent: ${st}`}>
      {st === 'awaiting_human' ? <Hand className="w-2.5 h-2.5" /> : agent.pr_url ? <GitPullRequest className="w-2.5 h-2.5" /> : <Bot className="w-2.5 h-2.5" />}
      {label}
    </span>
  );
}

export default function WorkItemAgentBadge({
  workItem,
  isDark = false,
}: {
  workItem: WorkItem;
  isDark?: boolean;
}) {
  const agent = agentOf(workItem);
  if (!agent?.status) return null;

  const itemNum = workItem.item_number || '';
  const jwt = typeof window !== 'undefined'
    ? (() => {
        try {
          const t = JSON.parse(localStorage.getItem('auth_tokens') || '{}');
          return t.access_token || t.session_token || '';
        } catch { return ''; }
      })()
    : '';
  const shellUrl = jwt && itemNum
    ? `${gavShellBase()}/gav-shell/?token=${encodeURIComponent(jwt)}&view=issues&item=${encodeURIComponent(itemNum)}`
    : null;

  const st = agent.status;
  const chip =
    st === 'working' || st === 'queued' ? ['Agent working…', 'bg-blue-600/20 text-blue-300 border-blue-700/50'] :
    st === 'awaiting_human' ? ['Needs you', 'bg-orange-600/20 text-orange-300 border-orange-700/50'] :
    st === 'pr_opened' ? ['PR ready', 'bg-emerald-600/20 text-emerald-300 border-emerald-700/50'] :
    st === 'done' || st === 'fixed' ? ['Agent done', 'bg-emerald-600/20 text-emerald-300 border-emerald-700/50'] :
    st === 'failed' || st === 'blocked' ? ['Agent failed', 'bg-rose-600/20 text-rose-300 border-rose-700/50'] :
    [`Agent: ${st}`, 'bg-gray-600/20 text-gray-300 border-gray-600/50'];

  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 ${isDark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-gray-50'}`}>
      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${chip[1]}`}>
        {st === 'awaiting_human' ? <Hand className="h-3 w-3" /> : <Bot className="h-3 w-3" />}
        {chip[0]}
      </span>
      {agent.action_required?.detail && (
        <span className={`text-[11px] ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
          {agent.action_required.detail}
        </span>
      )}
      {agent.pr_url && (
        <a href={agent.pr_url} target="_blank" rel="noopener noreferrer"
          className={`inline-flex items-center gap-1 text-[11px] font-medium hover:underline ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
          <GitPullRequest className="h-3 w-3" />PR
        </a>
      )}
      {shellUrl && (
        <a href={shellUrl} target="_blank" rel="noopener noreferrer"
          className={`ml-auto inline-flex items-center gap-1 text-[11px] font-medium hover:underline ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
          Open in My Shell<ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}
